import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

// Searchable ticker picker: type a company name (e.g. "nvidia") and pick the instrument.
export default function TickerSearch({ value, name, onSelect, placeholder, testid = "ticker-search" }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(value ? (name ? `${value} · ${name}` : value) : "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState(!!value);
  const ref = useRef();

  useEffect(() => {
    if (value) { setQuery(name ? `${value} · ${name}` : value); setChosen(true); }
    else { setQuery(""); setChosen(false); }
  }, [value, name]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (chosen) return;
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await api.search(q); setResults(r.data); setOpen(true); } catch {} finally { setLoading(false); }
    }, 320);
    return () => clearTimeout(t);
  }, [query, chosen]);

  const pick = (r) => {
    setChosen(true);
    setQuery(`${r.ticker} · ${r.name}`);
    setOpen(false);
    onSelect({ ticker: r.ticker, name: r.name, asset_type: r.asset_type });
  };

  const clear = () => {
    setChosen(false); setQuery(""); setResults([]); setOpen(false);
    onSelect({ ticker: "", name: "", asset_type: "" });
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
        <input
          data-testid={testid}
          className="input pl-10 pr-9"
          placeholder={placeholder || t("dialogs.searchTicker")}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setChosen(false); }}
          onFocus={() => { if (!chosen && results.length) setOpen(true); }}
        />
        {loading ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-500 animate-spin" size={16} />
        ) : chosen ? (
          <button type="button" onClick={clear} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-rose-500"><X size={16} /></button>
        ) : null}
      </div>
      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.15 }}
            data-testid="ticker-search-results"
            className="absolute z-50 mt-2 w-full max-h-64 overflow-y-auto bg-white rounded-2xl border border-slate-200/70 shadow-soft"
          >
            {results.map((r) => (
              <button
                type="button" key={`${r.ticker}-${r.exchange}`}
                data-testid={`ticker-option-${r.ticker}`}
                onClick={() => pick(r)}
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-brand-50 transition-colors text-left"
              >
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{r.ticker}</div>
                  <div className="text-xs text-slate-500 truncate">{r.name}</div>
                </div>
                <span className="pill bg-slate-100 text-slate-500 ml-2 shrink-0">{r.asset_type}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
